import { Flex, Typography } from '@paintscout/ui'
import { escapeHref } from '@paintscout/util/util'
import * as Sentry from '@sentry/react'
import React from 'react'
import { AuthCallback } from 'react-auth0'
import { Link, Redirect } from 'react-router-dom'
import { UNEXPECTED_ERROR_MESSAGE } from 'shared/util/auth/authRecovery'
import { consumeBranchRedirectUrl } from 'shared/util/routing'

/**
 * After auth0 authenticates it will redirect here
 */
function LoginCallback() {
  return (
    <div style={{ height: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <AuthCallback>
        {({ loading, error, previousUrl }) => {
          if (error) {
            // `api.access.deny()` in an Auth0 Action (deactivated user, banned company, invalid user)
            // returns `access_denied` with a readable reason. Legacy Rules returned `unauthorized`.
            // Both are expected outcomes, so show the reason instead of the unexpected-error dialog.
            if (error.error === 'unauthorized' || error.error === 'access_denied') {
              // @ts-ignore - error is on error_description field
              const msg = error?.error_description ?? 'Unauthorized'
              return (
                <Flex direction="column" align="center">
                  <Typography variant="h2" gutterBottom>
                    {msg}
                  </Typography>
                  <Typography>
                    <Link to="/logout">Click here to try logging in again</Link>
                  </Typography>
                </Flex>
              )
            }

            // This branch has never reported to Sentry (ENG-1190) — every prior fix on that ticket
            // targeted a different, already-instrumented failure mode, leaving whatever auth0-spa-js
            // throws here (e.g. a stale/overwritten PKCE transaction from a second concurrent login)
            // unobserved. Tag the raw Auth0 error code so the next occurrence is diagnosable.
            console.error(error)
            Sentry.captureException(error, {
              tags: { message: 'LoginCallback unexpected error', authError: error?.error }
            })
            return <Redirect to={{ pathname: '/logout', state: { message: UNEXPECTED_ERROR_MESSAGE } }} />
          }

          if (loading) {
            return null
          }

          // Branch deploys live at /_branch/{name}/ — use a hard redirect so the
          // browser loads the branch app instead of trying to route within the canonical
          // app. Covers both a previousUrl captured on a branch (session expiry) and the
          // post-logout return stashed by Logout (Auth0 can only return to the canonical origin).
          const branchRedirectUrl = consumeBranchRedirectUrl(previousUrl)
          if (branchRedirectUrl) {
            window.location.href = escapeHref(branchRedirectUrl)
            return null
          }

          return <Redirect to={previousUrl || '/'} />
        }}
      </AuthCallback>
    </div>
  )
}

export default LoginCallback
